import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PathViewLayout } from "./PathViewLayout/PathViewLayout";
import { PathUnknownTile } from "./PathUnknownTile/PathUnknownTile";
import { BadgeRequirementTileV2 } from "../../../../common/components/RequirementTiles/BadgeRequirementTileV2/BadgeRequirementTileV2";
import { LSRequalifiedBadgeTile } from "../LSRequalifiedBadgeTile/LSRequalifiedBadgeTile";
import { AnnualVolumeRequirementTile } from "../../../../common/components/RequirementTiles/AnnualVolumeRequirementTile/AnnualVolumeRequirementTile";
import { GrowthOrMaintainFQWithGPV } from "../../../../common/components/GrowthOrMaintainFQWithGPV/GrowthOrMaintainFQWithGPV";
import { NewLegTile } from "../../../../common/components/RequirementTiles/NewLegTile/NewLegTile";
import { LSQMonthsTile } from "../LSQMonthsTile";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { formatKpiValue } from "../../../../common/util/format";
import { useAppSelector } from "../../../../store";
import defaultContent from "../../../../content/leadershipSeminar";
import { FQ_GROWTH_TARGET, buildPathRequirements } from "./pathRequirements";
import type { Path } from "./types";
import type {
  LSPathViewResponse,
  LSPathEntry,
  LSPathRequirementsMap,
  LSPathVolumeRequirement,
} from "../../../../services/LeadershipSeminar/leadershipSeminarAPI.types";
import "./LSPathView.scss";

export interface LSPathViewProps {
  lsPathViewResponse: LSPathViewResponse;
  selectedPeriod: number;
  isLoading: boolean;
}

const findRequirement = <T extends { Name: string }>(arr: T[] | undefined, name: string): T | undefined =>
  arr?.find((r) => r.Name === name);

// ---------------------------------------------------------------------------
// Helper to get badge title from content
// ---------------------------------------------------------------------------

const getBadgeTitle = (reqName: string, t: ReturnType<typeof useTranslation>["t"]): string => {
  const badgesContent = defaultContent.pathView.badges as Record<string, { title: string; description: string }>;
  const badgeContent = badgesContent[reqName];

  if (badgeContent) {
    return String(t(`pathView.badges.${reqName}.title`, badgeContent.title));
  }
  return reqName;
};

// ---------------------------------------------------------------------------
// Tile renderer helper functions
// ---------------------------------------------------------------------------

interface TileRenderOptions {
  isoCountryCode: string;
  locale: string;
  selectedPeriod: number;
  t: ReturnType<typeof useTranslation>["t"];
}

const renderBadgeTile = (
  reqName: string,
  requirementsMap: LSPathRequirementsMap,
  t: ReturnType<typeof useTranslation>["t"],
): JSX.Element | null => {
  const data = findRequirement(requirementsMap.Badge, reqName);
  if (!data) return null;

  return (
    <BadgeRequirementTileV2
      title={getBadgeTitle(reqName, t)}
      status={data.Status}
      enableGarTracking={false}
      circleCheckmarkId={`ls-path-badge-${data.Name}`}
    />
  );
};

const renderRequalifiedBadgeTile = (
  reqName: string,
  requirementsMap: LSPathRequirementsMap,
  selectedPeriod: number,
): JSX.Element | null => {
  const data = findRequirement(requirementsMap.RequalifiedBadge, reqName);
  if (!data) return null;

  return <LSRequalifiedBadgeTile requirement={data} selectedPeriod={selectedPeriod} />;
};

const renderVolumeTile = (
  reqName: string,
  requirementsMap: LSPathRequirementsMap,
  options: TileRenderOptions,
): JSX.Element | null => {
  const data = findRequirement(requirementsMap.Volume, reqName);
  if (!data) return null;

  const volumeType = data.Name as LSPathVolumeRequirement["Name"] & ("personal" | "group" | "ruby" | "annualGpv");
  const monthlyTarget = data.Volume.Target.Actual / 12;
  const formattedMonthlyTarget = formatKpiValue(volumeType, monthlyTarget, options.isoCountryCode, options.locale);
  const volumeTypeLabels = defaultContent.pathView.volumeTypeLabels;
  const volumeTypeLabel = options.t(
    `pathView.volumeTypeLabels.${volumeType}`,
    volumeTypeLabels[volumeType as keyof typeof volumeTypeLabels] ?? volumeType,
  );

  const customDescription = String(
    options.t("pathView.volumeTileDescription", defaultContent.pathView.volumeTileDescription, {
      volumeType: volumeTypeLabel,
      monthlyTarget: formattedMonthlyTarget,
    }),
  );

  return (
    <AnnualVolumeRequirementTile
      isLoading={false}
      currentAmount={{ actual: data.Volume.Current.Actual, formatted: data.Volume.Current.Formatted }}
      targetAmount={{ actual: data.Volume.Target.Actual, formatted: data.Volume.Target.Formatted }}
      met={data.Met}
      volumeType={volumeType}
      exempt={data.Exempt}
      customDescription={customDescription}
    />
  );
};

const renderMaintainFQOrGrowGPVTile = (reqName: string, requirementsMap: LSPathRequirementsMap): JSX.Element | null => {
  const data = findRequirement(requirementsMap.MaintainFQOrGrowGPV, reqName);
  if (!data) return null;

  // The live API returns Grow (FQ) + Maintain (GPV); the component consumes
  // GrowthFQ (plain FQ-count numbers) + AnnualGPV (formatted GPV amounts).
  // Both API objects expose their running total as `Current`, which maps to
  // the component's `YTDQCount` field.
  const { Grow, Maintain } = data;
  const growthFQ = {
    Status: Grow.Status,
    Met: Grow.Met,
    Baseline: Grow.Baseline,
    YTDQCount: Grow.Current,
    Grow: Grow?.Grow,
    Target: Grow.Target,
    Tracking: {
      Growth: Grow.Tracking,
      Maintain: Grow.Tracking,
    },
  };

  const annualGPV = {
    Status: Maintain.Status,
    Met: Maintain.Met,
    Target: Maintain.Target,
    Baseline: Maintain.Baseline,
    RequiredGPV: Maintain.RequiredGPV,
    YTDQCount: Maintain.Current,
    Projected: Maintain.Projected,
  };

  return <GrowthOrMaintainFQWithGPV GrowthFQ={growthFQ} AnnualGPV={annualGPV} />;
};

const renderNewLegTile = (reqName: string, requirementsMap: LSPathRequirementsMap): JSX.Element | null => {
  const data = findRequirement(requirementsMap.NewLeg, reqName);
  if (!data) return null;

  return <NewLegTile requirement={data} />;
};

const renderQCountTile = (
  reqName: string,
  requirementsMap: LSPathRequirementsMap,
  options: TileRenderOptions,
): JSX.Element | null => {
  const data = findRequirement(requirementsMap.QCount, reqName);
  if (!data) return null;

  return <LSQMonthsTile requirement={data} selectedPeriod={options.selectedPeriod} />;
};

// ---------------------------------------------------------------------------
// Build the expanded View JSX for a single path
// ---------------------------------------------------------------------------

const buildPathExpandedView = (
  pathEntry: LSPathEntry,
  requirementsMap: LSPathRequirementsMap,
  options: TileRenderOptions,
): JSX.Element => {
  const rows: JSX.Element[] = [];

  pathEntry.ViewLayout.Row.forEach((row, rowIndex) => {
    const rowTiles: JSX.Element[] = [];

    row.forEach((reqName, colIndex) => {
      const pathReq = pathEntry.Requirements.find((r) => r.ReqName === reqName);
      if (!pathReq) return;

      let tile: JSX.Element | null = null;
      let spanColumns = 1;
      let noStretch = false;

      switch (pathReq.ReqType) {
        case "Badge":
          tile = renderBadgeTile(reqName, requirementsMap, options.t);
          noStretch = true;
          break;
        case "RequalifiedBadge":
          tile = renderRequalifiedBadgeTile(reqName, requirementsMap, options.selectedPeriod);
          break;
        case "Volume":
          tile = renderVolumeTile(reqName, requirementsMap, options);
          spanColumns = 2;
          break;
        case "MaintainFQOrGrowGPV":
          tile = renderMaintainFQOrGrowGPVTile(reqName, requirementsMap);
          spanColumns = 2;
          break;
        case "NewLeg":
          tile = renderNewLegTile(reqName, requirementsMap);
          break;
        case "QCount":
          tile = renderQCountTile(reqName, requirementsMap, options);
          break;
        default:
          tile = <PathUnknownTile reqName={reqName} reqType={pathReq.ReqType} />;
      }

      if (tile) {
        const tileClassName = `LSPathView__tile${spanColumns === 2 ? " LSPathView__tile--span-2" : ""}${noStretch ? " LSPathView__tile--no-stretch" : ""}`;

        rowTiles.push(
          <div key={`${rowIndex}-${colIndex}-${reqName}`} className={tileClassName}>
            {tile}
          </div>,
        );
      }
    });

    if (rowTiles.length > 0) {
      rows.push(
        <div key={`row-${rowIndex}`} className="LSPathView__row">
          {rowTiles}
        </div>,
      );
    }
  });

  return <div className="LSPathView__tiles">{rows}</div>;
};

// ---------------------------------------------------------------------------
// LSPathView — market-agnostic path view renderer
//
// Any future market using the same /lsi/pathview API shape routes here
// automatically — no new component needed, just a config + StrategyName entry.
// ---------------------------------------------------------------------------

export const LSPathView = ({ lsPathViewResponse, selectedPeriod, isLoading }: LSPathViewProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);
  const { user } = useAppSelector((store) => store.boot);
  const { loginFields } = useAppSelector((store) => store.loginLocalStorage);
  const pathList = lsPathViewResponse?.Path ?? [];
  // Prefer the API-designated default path, falling back to the first path
  const defaultPathViewId = (pathList.find((p) => p.Default) ?? pathList[0])?.ViewId ?? "";

  const [activeViewId, setActiveViewId] = useState<string>(defaultPathViewId);

  // Sync activeViewId when the default path ID changes (data load/reload)
  useEffect(() => {
    if (defaultPathViewId) {
      setActiveViewId(defaultPathViewId);
    }
  }, [defaultPathViewId]);

  // Show skeleton cards while loading (4 cards in a row + expanded view)
  if (isLoading) {
    return (
      <div className="LSPathView LSPathView--loading" aria-busy="true">
        <div className="LSPathView__progress-section">
          <LeafRectangleLoader isLoading height="1.5rem" width="16rem" />
        </div>
        <div className="LSPathView__skeleton-cards">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="LSPathView__skeleton-card">
              <div className="LSPathView__skeleton-header">
                <LeafRectangleLoader isLoading height="1.5rem" width="4rem" />
                <LeafRectangleLoader isLoading height="1.5rem" width="5rem" />
              </div>
              <div className="LSPathView__skeleton-divider" />
              <div className="LSPathView__skeleton-requirements">
                <LeafRectangleLoader isLoading height="3rem" width="4rem" />
                <LeafRectangleLoader isLoading height="3rem" width="4rem" />
              </div>
            </div>
          ))}
        </div>
        {/* Expanded view skeleton */}
        <div className="LSPathView__skeleton-expanded">
          <div className="LSPathView__skeleton-expanded-header">
            <LeafRectangleLoader isLoading height="1.5rem" width="60%" />
          </div>
          <div className="LSPathView__skeleton-expanded-content">
            <div className="LSPathView__skeleton-tile">
              <LeafRectangleLoader isLoading height="1.25rem" width="50%" />
              <LeafRectangleLoader isLoading height="4rem" width="100%" />
            </div>
            <div className="LSPathView__skeleton-tile">
              <LeafRectangleLoader isLoading height="1.25rem" width="50%" />
              <LeafRectangleLoader isLoading height="4rem" width="100%" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pathList.length) return null;

  const tileRenderOptions: TileRenderOptions = {
    isoCountryCode: user.isoCountryCode,
    locale: loginFields.locale,
    selectedPeriod,
    t,
  };

  const paths: Path[] = pathList.map((pathEntry) => ({
    ViewId: pathEntry.ViewId,
    Status: pathEntry.Status,
    Description: pathEntry.Description,
    Requirements: buildPathRequirements(pathEntry, lsPathViewResponse.Requirements),
    View: buildPathExpandedView(pathEntry, lsPathViewResponse.Requirements, tileRenderOptions),
  }));

  return (
    <div className="LSPathView">
      {/* How do I qualify? Track your progress section */}
      <div className="LSPathView__progress-section">
        <h2 className="LSPathView__progress-title">
          {String(t("requirements.pathViewSectionHeader", defaultContent.requirements.pathViewSectionHeader))}
        </h2>
      </div>
      <PathViewLayout ActiveViewId={activeViewId} Paths={paths} onPathSelect={setActiveViewId} />
    </div>
  );
};
